import type {
  ColumnDef,
  OnChangeFn,
  RowSelectionState,
  VisibilityState,
} from "@tanstack/react-table"
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { Columns3Icon } from "lucide-react"
import type { ReactNode } from "react"
import { Fragment, useMemo, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data?: TData[]
  expandedChildren?: (props: { data: TData }) => JSX.Element
  onSelectionRow?: OnChangeFn<RowSelectionState>
  rowSelection?: RowSelectionState
  showColumnVisibility?: boolean
  actionChildren?: ReactNode
  withFooter?: boolean
  /** Altura máxima opcional para ativar header sticky + scroll */
  maxHeight?: number | string
}
export function DataTable<TData, TValue>({
  columns,
  data = [],
  expandedChildren,
  onSelectionRow,
  rowSelection,
  showColumnVisibility,
  actionChildren,
  withFooter,
  maxHeight = "calc(100vh - 300px)",
}: DataTableProps<TData, TValue>) {
  const [columnVisibility, setColumnVisibility] =
    useState<VisibilityState>({})
  const [selectedRows, setSelectedRows] =
    useState<RowSelectionState>({})
  const processedColumns = useMemo(() => {
    if (!expandedChildren) {
      return columns.filter((c) => c.id !== "expander")
    }
    return columns
  }, [columns, expandedChildren])
  const safeData = useMemo(() => data, [data])
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: safeData,
    columns: processedColumns,
    getRowCanExpand: () => Boolean(expandedChildren),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: onSelectionRow || setSelectedRows,
    state: {
      columnVisibility,
      rowSelection: rowSelection || selectedRows,
    },
  })
  return (
    <div>
      {(showColumnVisibility || actionChildren) && (
        <div className="mb-4 flex flex-col items-end justify-end gap-2 sm:flex-row sm:items-start">
          {showColumnVisibility && (
            <ToggleColumnVisibility table={table} />
          )}
          {actionChildren}
        </div>
      )}
      <div className="overflow-hidden rounded-md border border-border">
        <div
          className="overflow-auto"
          style={{
            maxHeight:
              typeof maxHeight === "number"
                ? `${maxHeight}px`
                : maxHeight,
          }}
        >
          <Table
            divClassname="overflow-visible"
            className="border-separate border-spacing-0 [&_td]:border-border [&_tfoot_td]:border-t [&_th]:border-b [&_th]:border-border [&_tr:not(:last-child)_td]:border-b [&_tr]:border-none"
          >
            <TableHeader className="sticky top-0 z-10 bg-background/90 backdrop-blur-sm">
              {table
                .getHeaderGroups()
                .map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="bg-sidebar hover:bg-sidebar"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{ width: header.getSize() }}
                      >
                        {header.isPlaceholder ? null : (
                          <span className="truncate">
                            {flexRender(
                              header.column.columnDef
                                .header,
                              header.getContext()
                            )}
                          </span>
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <Fragment key={row.id}>
                    <TableRow
                      data-state={
                        row.getIsSelected() && "selected"
                      }
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className="whitespace-nowrap [&:has([aria-expanded])]:w-px [&:has([aria-expanded])]:py-0 [&:has([aria-expanded])]:pr-0"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                    {row.getIsExpanded() && (
                      <TableRow>
                        <TableCell
                          colSpan={
                            row.getVisibleCells().length
                          }
                        >
                          {expandedChildren?.({
                            data: row.original,
                          })}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={processedColumns.length}
                    className="h-24 text-center"
                  >
                    Nenhum resultado encontrado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {withFooter && (
              <TableFooter>
                {table
                  .getFooterGroups()
                  .map((footerGroup) => (
                    <TableRow key={footerGroup.id}>
                      {footerGroup.headers.map((header) => (
                        <TableCell key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef
                                  .footer,
                                header.getContext()
                              )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableFooter>
            )}
          </Table>
        </div>
      </div>
    </div>
  )
}
interface ToggleColumnVisibilityProps<TData> {
  table: ReturnType<typeof useReactTable<TData>>
}
export function ToggleColumnVisibility<TData>({
  table,
}: ToggleColumnVisibilityProps<TData>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <Columns3Icon
            className="-ms-1 me-2 opacity-60"
            size={16}
            strokeWidth={2}
            aria-hidden="true"
          />
          Ver colunas
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>
          Alternar colunas
        </DropdownMenuLabel>
        {table
          .getAllColumns()
          .filter((column) => column.getCanHide())
          .map((column) => (
            <DropdownMenuCheckboxItem
              key={column.id}
              checked={column.getIsVisible()}
              onCheckedChange={(value) =>
                column.toggleVisibility(!!value)
              }
              onSelect={(event) => event.preventDefault()}
            >
              {typeof column.columnDef?.header === "string"
                ? column.columnDef.header
                : column.id}
            </DropdownMenuCheckboxItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
